<script setup lang="ts">
import { Monitor, Moon, Sun } from 'lucide-vue-next';
import type { GuideAnchorAttrs } from '@/composables/guide/useGuideForm';
import { useAppearance } from '@/composables/useAppearance';
import { APPEARANCES } from '@/config/constants/appearance';

interface Props {
    class?: string;
    iconsOnly?: boolean;
    /** The Guide's anchor attributes; only the Appearance settings page passes them. */
    guideAttrs?: GuideAnchorAttrs;
}

const { class: containerClass = '', iconsOnly = false, guideAttrs = undefined } = defineProps<Props>();

const { t } = useI18n();
const { appearance, updateAppearance } = useAppearance();

const tabs = [
    { value: APPEARANCES.LIGHT, Icon: Sun, label: t('settings.appearance.light') },
    { value: APPEARANCES.DARK, Icon: Moon, label: t('settings.appearance.dark') },
    { value: APPEARANCES.SYSTEM, Icon: Monitor, label: t('settings.appearance.system') },
] as const;
</script>

<template>
    <div :class="['bg-muted inline-flex gap-1 rounded-sm p-1', containerClass]" v-bind="guideAttrs">
        <button
            v-for="{ value, Icon, label } in tabs"
            :key="value"
            :aria-pressed="appearance === value"
            @click="updateAppearance(value)"
            :class="[
                'flex items-center rounded-sm transition-colors',
                iconsOnly ? 'justify-center p-2' : 'px-3.5 py-1.5',
                appearance === value ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
            ]"
            :title="iconsOnly ? label : undefined"
        >
            <component :is="Icon" :class="iconsOnly ? 'h-3 w-3' : '-ml-1 h-4 w-4'" />
            <span v-if="!iconsOnly" class="ml-1.5 text-sm">{{ label }}</span>
        </button>
    </div>
</template>
